'use client'

import { Search, Terminal } from 'lucide-react'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import { useState } from 'react'

import { AuraIconMono } from '@/components/icons'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

import { models } from './models-data'

const getModelImage = (model: any) => {
  const imageMap: { [key: string]: string } = {
    // DTU logo for SignalP and DeepLoc
    'signalp-6-0': '/models/dtu.jpg',
    'deeploc': '/models/dtu.jpg',
    
    // Westlake logo for Evolla, ProTrek, DeNovo-Pinal, SaProt
    'evolla-llama3-8b': '/models/westlake.png',
    'protrek': '/models/westlake.png',
    'denovo-pinal': '/models/westlake.png',
    'saprot': '/models/westlake.png',
    
    // Match by name for others
    'alphafold-2': '/models/alphafold2.png',
    'boltz-1': '/models/boltz.png',
    'boltz-2': '/models/boltz.png',
    'simple-fold': '/models/simplefold.jpeg',
    'esm-1b': '/models/esm.png',
    'openmm': '/models/openMM.png',
    'amp-net': '/models/amp-net.svg'
  }
  return imageMap[model.id] || '/fold.png'
}

function normalizeModelKey(raw: string) {
  return String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/_/g, '')
    .replace(/-/g, '')
}

function getUseCta(model: any): { label: string; href: string; icon: any } | null {
  const id = String(model?.id || '')
  const name = String(model?.name || '')
  const key = `${normalizeModelKey(id)}|${normalizeModelKey(name)}`

  const foldIds = new Set(['alphafold-2', 'boltz-1', 'boltz-2', 'simple-fold', 'esm-1b'])
  const designIds = new Set(['denovo-pinal', 'saprot'])
  const propertiesIds = new Set(['signalp-6-0', 'amp-net', 'deeploc'])

  if (foldIds.has(id)) return { label: 'Use in Sandbox', href: '/fold/new', icon: Terminal }
  if (designIds.has(id) || key.includes('denovopinal') || key.includes('saprot'))
    return { label: 'Use in Sandbox', href: '/design/new', icon: Terminal }
  if (propertiesIds.has(id) || key.includes('signalp') || key.includes('deeploc') || key.includes('ampinet') || key.includes('ampnet'))
    return { label: 'Use in Sandbox', href: '/properties/new', icon: Terminal }
  if (key.includes('evolla') && key.includes('llama3') && key.includes('8b')) return { label: 'Use in Agent', href: '/agent', icon: AuraIconMono }

  return null
}

export default function ModelsPage() {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const router = useRouter()

  const filteredModels = models.filter(model => {
    const matchesSearch = model.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         model.description.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesCategory = selectedCategory === 'all' || model.categories.includes(selectedCategory)
    return matchesSearch && matchesCategory
  })

  // Get all unique categories from all models
  const allCategories = Array.from(new Set(models.flatMap(model => model.categories)))
  const categories = allCategories.sort()

  return (
    <div className="flex flex-col gap-6 p-2 lg:p-4">
      {/* Search and Filter Controls */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search models"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 w-[373px]"
            />
          </div>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Select categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((category) => (
                <SelectItem key={category} value={category}>
                  {category}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Models List */}
      <div className="rounded-xl border bg-background overflow-hidden">
        {filteredModels.map((model, idx) => {
          const useCta = getUseCta(model)
          const categoryLine = Array.isArray(model.categories) && model.categories.length > 0
            ? model.categories.join(' · ')
            : String(model.type || '')

          return (
            <div
              key={model.id}
              className={`flex items-center gap-4 px-4 py-4 ${idx < filteredModels.length - 1 ? 'border-b' : ''} cursor-pointer hover:bg-muted/30 transition-colors`}
              onClick={() => router.push(`/models/${model.id}`)}
            >
              <div className="h-10 w-10 flex items-center justify-center rounded-full overflow-hidden bg-muted/60 shrink-0">
                <Image
                  src={getModelImage(model)}
                  alt={model.name}
                  width={40}
                  height={40}
                  className="object-cover w-full h-full"
                  priority={idx < 6}
                  onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                    console.error('Image failed to load:', model.name)
                    e.currentTarget.style.display = 'none'
                  }}
                />
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="truncate text-[15px] font-semibold text-foreground">{model.name}</div>
                  {model.version ? (
                    <Badge variant="secondary" className="shrink-0 text-xs font-medium">
                      {model.version}
                    </Badge>
                  ) : null}
                </div>
                <div className="mt-0.5 truncate text-[13px] text-muted-foreground">
                  {categoryLine}
                </div>
              </div>

              <div className="shrink-0">
                {useCta ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 px-3 text-sm inline-flex items-center gap-2"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      router.push(useCta.href)
                    }}
                  >
                    <useCta.icon className="h-4 w-4" />
                    <span>{useCta.label}</span>
                  </Button>
                ) : null}
              </div>
            </div>
          )
        })}
      </div>

      {/* Empty State */}
      {filteredModels.length === 0 && (
        <div className="text-center py-12">
          <p className="text-muted-foreground">No models found matching your criteria.</p>
        </div>
      )}
    </div>
  )
}
